import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { Loader2 } from 'lucide-react';

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * Enhanced component that redirects to the unauthorized page if the user is not authenticated
 * Now uses the centralized auth context for better performance and no race conditions
 */
const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const location = useLocation();

  // Only log important state changes, not every render
  const shouldLog = authLoading || !isAuthenticated;
  if (shouldLog) {
    console.log('[RequireAuth] Auth state:', {
      isAuthenticated,
      authLoading,
      hasUser: !!user,
      userId: user?.id,
      location: location.pathname
    });
  }

  // If still loading auth state, show loading spinner
  if (authLoading) {
    console.log('[RequireAuth] Loading authentication...');
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
        <p className="text-sm text-muted-foreground">
          Loading authentication...
        </p>
      </div>
    );
  }

  // If not authenticated, redirect to the unauthorized page
  if (!isAuthenticated) {
    console.log("[RequireAuth] User not authenticated, redirecting to unauthorized page");
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }
  
  // Render children for authenticated users (no logging for successful auth)
  return <>{children}</>;
};

export default RequireAuth;
